import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import { isEqual } from "lodash";
import { isValidDate } from "pages/file/utils/helper";
import React from "react";
import { useCompanyForm } from "utils/context/CompanyFormContext";
import {
  JacketFormType,
  ProductType,
} from "utils/data/enum";
import useEffectiveDateValidation from "./useEffectiveDateValidation";
import useFormWrapper from "./useFormWrapper";

export interface SelectFieldOptionForms extends SelectFieldOption {
  formID: string;
  formVersion: number | string;
  formTemplateName: string;
  formPrefix: string;
  formType: string;
  agencyExclusive: string;
  liabilityAmount: number | null;
}

export default function useForms(
  productType: ProductType,
  formType: JacketFormType | string,
  defaultDate: Date | undefined,
  isFieldDisabled: boolean | undefined,
) {
  const [options, setOptions] = React.useState<SelectFieldOptionForms[]>([]);
  const optionsRef = React.useRef<SelectFieldOptionForms[]>([]);

  const [, { getForms }] = useCompanyForm();
  const { validateEffectiveDate } = useEffectiveDateValidation();
  const { getValues } = useFormWrapper();
  const [companyId, stateId, isReadOnly] = getValues([
    "agency.id",
    "properties.0.state.abbr",
    "isReadOnly"
  ]);

  // Callers that source the effective date from useWatch get a *cloned* value on
  // every form change — RHF's clone rebuilds Dates (`new Date(value)`), so the
  // Date arrives with a fresh identity after any edit anywhere on the form. Using
  // the object as an effect dependency therefore refetched GetForms on every
  // keystroke. Key the effect on the timestamp so it only re-runs when the
  // effective date genuinely changes.
  const defaultDateKey =
    defaultDate instanceof Date ? defaultDate.getTime() : defaultDate;

  React.useEffect(() => {
    async function getFormsAsync() {

      let product = "";
      switch (productType) {
        case ProductType.Aal:
          product = "aALProducts";
          break;
        case ProductType.Cpl:
          product = "cpls";
          break;
        case ProductType.Jacket:
          product = "jackets";
          break;
      }

      const validationMessage = await validateEffectiveDate(product, undefined, defaultDate, false, false);
      if (validationMessage && !isReadOnly) {
        setOptions([]);
        return;
      }

      const companyForms = await getForms(
        companyId,
        stateId,
        productType,
        formType,
        defaultDate
      );

      if (!companyForms || companyForms.length === 0) {
        setOptions([]);
        return;
      }

      const options = companyForms.map((form) => {
        const option: SelectFieldOptionForms = {
          text: form.formName,
          value: form.formName,
          formID: form.formID,
          formVersion: form.formVersion,
          formTemplateName: form.formTemplateName,
          formPrefix: form.formPrefix,
          formType: form.formType,
          opaIdentifier: form.opaIdentifier,
          agencyExclusive: form.agencyExclusive,
          liabilityAmount: form.liabilityAmount,
          selected: form.default === 1,
        };

        return option;
      });
      setOptions(options);
    }

    if (!companyId || !stateId || !formType || !defaultDate || !isValidDate(defaultDate) || isFieldDisabled) {
      setOptions([]);
      return;
    }
    getFormsAsync();

    // defaultDate is intentionally absent: defaultDateKey stands in for it, so an
    // equal date with a new identity does not retrigger the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, stateId, formType, productType, defaultDateKey, isFieldDisabled, getForms]);

  if (!isEqual(optionsRef.current, options)) {
    optionsRef.current = options;
  }

  return { options: optionsRef.current };
}
